import type { ReactNode } from 'react';
import { CaretLeft, CaretRight, CaretDown, Minus, Plus } from '@phosphor-icons/react';
import { css } from 'styled-system/css';

const rail = css({
  bg: 'ws.card',
  borderRadius: '16px',
  padding: '13px 16px 14px',
  boxShadow: '0 1px 2px rgba(33,75,64,0.05), 0 8px 22px rgba(33,75,64,0.10)',
  _dark: { boxShadow: '0 1px 2px rgba(0,0,0,0.3), 0 8px 22px rgba(0,0,0,0.35)' },
});
// Wrap-safe so a wide trailing affordance (e.g. the verify cooldown ring + label) never pushes the zoom cluster past the panel edge on narrow phones.
const topRow = css({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', rowGap: '8px', marginBottom: '9px' });
const labelGroup = css({ display: 'flex', alignItems: 'center', gap: '8px' });
const dot = css({ width: '7px', height: '7px', borderRadius: '999px', bg: 'ws.sakura', flexShrink: 0 });
const label = css({ fontFamily: 'wsUi', fontSize: '11px', fontWeight: 'bold', letterSpacing: '0.14em', color: '#6B520F',
  _dark: { color: '#CBBE83' }, display: 'inline-flex', alignItems: 'center', gap: '4px' });
const sep = css({ width: '1px', height: '11px', bg: 'rgba(76,72,36,0.22)', _dark: { bg: 'rgba(233,242,236,0.2)' } });
const counter = css({ fontFamily: 'wsUi', fontSize: '12px', fontWeight: 'semibold', color: 'ws.khaki', opacity: 0.7, whiteSpace: 'nowrap' });

const rightGroup = css({ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '10px', rowGap: '8px', minWidth: 0 });
const zoom = css({ display: 'flex', alignItems: 'center', bg: '#F2EDDC', borderRadius: '9px', overflow: 'hidden', _dark: { bg: '#2A362E' } });
const zoomBtn = css({ width: '32px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', bg: 'transparent', border: 'none', color: 'ws.jadeInk', cursor: 'pointer' });
const zoomSep = css({ width: '1px', height: '15px', bg: 'rgba(33,75,64,0.16)', _dark: { bg: 'rgba(233,242,236,0.14)' } });

const mainRow = css({ display: 'flex', alignItems: 'center', gap: '10px' });
const stepper = css({
  flex: 'none',
  width: '40px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '13px',
  bg: '#F2EDDC',
  color: 'ws.jadeInk',
  _dark: { bg: '#2A362E' },
  fontSize: '18px',
  cursor: 'pointer',
  _disabled: { opacity: 0.4, cursor: 'not-allowed' },
});
const clueText = css({ flex: 1, textAlign: 'center', fontFamily: 'wsUi', fontWeight: 'bold', fontSize: '20px', lineHeight: '1.12', letterSpacing: '-0.01em', color: 'ws.jadeInk' });
// Subdued word-length helper trailing the clue, e.g. "(5)" — a hint, not part of the clue text.
const countChip = css({ marginInlineStart: '0.4em', fontWeight: 'semibold', fontSize: '0.75em', color: 'ws.khaki', opacity: 0.55, fontVariantNumeric: 'tabular-nums' });
const srOnly = css({ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 });

export type ClueDirection = 'horizontal' | 'vertical';

export interface ClueRailProps {
  readonly direction: ClueDirection;
  readonly directionLabel: string;
  readonly clue: string;
  // Word length shown as a subdued "(N)" after the clue; omit to hide.
  readonly letterCount?: number;
  // Spoken form of letterCount for screen readers (e.g. "5 lettres"); the visible "(N)" is aria-hidden.
  readonly letterCountLabel?: string;
  readonly index: number;
  readonly total: number;
  readonly groupLabel: string;
  readonly counterLabel: string;
  readonly prevLabel: string;
  readonly nextLabel: string;
  readonly zoomInLabel: string;
  readonly zoomOutLabel: string;
  readonly onPrev?: () => void;
  readonly onNext?: () => void;
  readonly onZoomIn?: () => void;
  readonly onZoomOut?: () => void;
  // Replaces the index/total counter in the label row (e.g. a hint control); index/total still drive prev/next bounds.
  readonly trailing?: ReactNode;
  // Optional action anchored to the far right of the label row (e.g. a report-clue trigger).
  readonly report?: ReactNode;
}

export function ClueRail({ direction, directionLabel, clue, letterCount, letterCountLabel, index, total, groupLabel, counterLabel, prevLabel, nextLabel, zoomInLabel, zoomOutLabel, onPrev, onNext, onZoomIn, onZoomOut, trailing, report }: ClueRailProps) {
  return (
    <div className={rail} role="group" aria-label={groupLabel}>
      <div className={topRow}>
        <div className={labelGroup}>
          <span aria-hidden="true" className={dot} />
          <span className={label}>
            {directionLabel}
            {direction === 'horizontal'
              ? <CaretRight aria-hidden="true" weight="bold" />
              : <CaretDown aria-hidden="true" weight="bold" />}
          </span>
          {trailing ? null : (
            <>
              <span aria-hidden="true" className={sep} />
              <span className={counter} aria-label={counterLabel}>{index} / {total}</span>
            </>
          )}
        </div>
        <div className={rightGroup}>
          {trailing}
          {report}
          <div className={zoom}>
            <button type="button" className={zoomBtn} onClick={onZoomOut} aria-label={zoomOutLabel}><Minus aria-hidden="true" weight="bold" /></button>
            <span aria-hidden="true" className={zoomSep} />
            <button type="button" className={zoomBtn} onClick={onZoomIn} aria-label={zoomInLabel}><Plus aria-hidden="true" weight="bold" /></button>
          </div>
        </div>
      </div>
      <div className={mainRow}>
        <button type="button" className={stepper} onClick={onPrev} disabled={!onPrev || total <= 1} aria-label={prevLabel}><CaretLeft aria-hidden="true" weight="bold" /></button>
        <div className={clueText}>
          {clue}
          {letterCount !== undefined ? (
            <>
              <span aria-hidden="true" className={countChip}>({letterCount})</span>
              {letterCountLabel ? <span className={srOnly}>{letterCountLabel}</span> : null}
            </>
          ) : null}
        </div>
        <button type="button" className={stepper} onClick={onNext} disabled={!onNext || total <= 1} aria-label={nextLabel}><CaretRight aria-hidden="true" weight="bold" /></button>
      </div>
    </div>
  );
}
